import React from 'react'
import { Pagination, Spin } from '@surecompanies/core_components'

import type {
  AllocationTicket,
  TransactionViewAllocation,
  TransactionViewRow,
} from '../../../api/transaction-details-api-client'
import {
  DisplayRow,
  GRID_MIN_WIDTH,
  PAGE_SIZE_OPTIONS,
  SortColumnKey,
  SortOrder,
} from '../helpers'
import TableHead from './TableHead'
import TransactionRow from './TransactionRow'
import styles from './TransactionsTable.module.scss'

/**
 * The sticky-header transaction grid + its pager. State/data-fetching all
 * live in useTransactionDetails; this component is a pure view over
 * `displayRows` (see helpers.ts#buildDisplayRows for the split-parent/child
 * flattening). Pagination mirrors BenefitsTable/index.tsx exactly (antd
 * `Pagination` + `showTotal`) instead of the design mock's bespoke ‹/› pager.
 */

interface TransactionsTableProps {
  displayRows: DisplayRow[]
  searchQuery: string
  isLoading: boolean
  isFetching: boolean
  total: number
  page: number
  limit: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onRowClick: (txn: TransactionViewRow) => void
  onOpenTickets: (tickets: AllocationTicket[]) => void
  onOpenAdjust: (
    allocation: TransactionViewAllocation,
    transactionId: string
  ) => void
  onRowAction: (row: DisplayRow) => void
  /** Opens the event-history drawer for a transaction. */
  onOpenHistory: (txn: TransactionViewRow) => void
  /** Opens the Adjust Payment modal on its Return section for this row. */
  onReturn: (row: DisplayRow) => void
  onUnreturn: (txn: TransactionViewRow) => void
  /** True when the caller has deliberately deselected every payment type. */
  noSourcesSelected: boolean
  sortBy: SortColumnKey | undefined
  sortOrder: SortOrder | undefined
  onToggleSort: (key: SortColumnKey) => void
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  displayRows,
  searchQuery,
  isLoading,
  isFetching,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  onRowClick,
  onOpenTickets,
  onOpenAdjust,
  onRowAction,
  onOpenHistory,
  onReturn,
  onUnreturn,
  noSourcesSelected,
  sortBy,
  sortOrder,
  onToggleSort,
}) => (
  <>
    <div className={styles.wrap}>
      <div
        className={`${styles.updatingBar}${isFetching ? ` ${styles.active}` : ''}`}
      />
      {/* A refetch keeps the previous page on screen; dim it and say so,
          or a slow filter looks like it did nothing. */}
      {isFetching && !isLoading && (
        <div className={styles.updatingOverlay} role="status">
          <Spin size="small" />
          <span>Updating…</span>
        </div>
      )}
      <div
        className={`${styles.scrollBox}${isFetching && !isLoading ? ` ${styles.stale}` : ''}`}
        aria-busy={isFetching}
      >
        <div style={{ minWidth: GRID_MIN_WIDTH }}>
          <TableHead
            sortBy={sortBy}
            sortOrder={sortOrder}
            onToggleSort={onToggleSort}
          />
          {isLoading ? (
            <div className={styles.emptyState}>Loading transactions…</div>
          ) : noSourcesSelected ? (
            <div className={styles.emptyState}>
              No payment types selected — choose at least one to see results.
            </div>
          ) : displayRows.length === 0 ? (
            <div className={styles.emptyState}>
              No transactions match the current filters.
            </div>
          ) : (
            displayRows.map((row) => (
              <TransactionRow
                key={row.key}
                row={row}
                searchQuery={searchQuery}
                onOpen={onRowClick}
                onOpenTickets={onOpenTickets}
                onOpenAdjust={onOpenAdjust}
                onRowAction={onRowAction}
                onOpenHistory={onOpenHistory}
                onReturn={onReturn}
                onUnreturn={onUnreturn}
              />
            ))
          )}
        </div>
      </div>
    </div>

    <div className={styles.pagBar}>
      <Pagination
        total={total}
        current={page}
        pageSize={limit}
        showSizeChanger
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onChange={(p, ps) => {
          onPageChange(p)
          onLimitChange(ps)
        }}
        showTotal={(t, [start, end]) => `${start}–${end} of ${t}`}
      />
    </div>
  </>
)

export default TransactionsTable
